import { FC } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { generalEnum } from '../../../../utils/generalEnum';
import TemplateDetails from '../TemplateDetails';
import { DetailsRow } from 'lib/ClientDataDetails/DetailsRow';
import EnergyLabel from './EnergyLabel';
import formatCityName from 'utils/formatCityName/formatCityName';
import { ClientDetailsRealEstate } from 'types/ClientDetails/ClientDetailsRealEstate';
import { ClientDetailsRealEstateTypeEnum } from 'types/ClientDetails/Enums/ClientDetailsRealEstateTypeEnum';
import { RealEstateRoleEnum } from 'types/ClientDetails/Enums/RealEstateRoleEnum';
import { RealEstateValuationTypeEnum } from 'types/ClientDetails/Enums/RealEstateValuationTypeEnum';

type RealEstateDetailsProps = {
  realEstate?: ClientDetailsRealEstate;
};

const RealEstateDetails: FC<RealEstateDetailsProps> = ({ realEstate }) => {
  const { t } = useTranslation();
  if (!realEstate) return null;
  const {
    type,
    location: address,
    valuation,
    role,
    constructionPlan,
    energyEfficiencyReport,
  } = realEstate;
  const tl = 'clients.clientData.realEstate';
  const addressLabel = address?.street
    ? `${address?.street} ${address?.houseNumber || ''} ${address?.houseNumberSuffix || ''}`
    : null;

  return (
    <Grid container data-testid="realEstateDetails-container">
      <Grid xs={12} md={6} item px={3} py={2}>
        <TemplateDetails title={t(`${tl}.realEstateTitle`)}>
          <DetailsRow
            label={t(`${tl}.typeOfRealEstate`)}
            value={generalEnum('typeOfRealEstate', type, ClientDetailsRealEstateTypeEnum)}
          />
          <DetailsRow
            label={t(`${tl}.realEstateRole`)}
            value={generalEnum('realEstateRole', role, RealEstateRoleEnum)}
          />
        </TemplateDetails>

        {!address || !Object.values(address).length ? null : (
          <TemplateDetails title={t(`${tl}.addressTitle`)}>
            <DetailsRow label={t(`${tl}.address`)} value={addressLabel} />
            <DetailsRow label={t(`${tl}.postalCode`)} value={address?.postalCode} />
            <DetailsRow label={t(`${tl}.city`)} value={formatCityName(address?.city)} />
          </TemplateDetails>
        )}

        {!constructionPlan || !Object.values(constructionPlan).length ? null : (
          <TemplateDetails title={t(`${tl}.constructionPlanTitle`)}>
            <DetailsRow label={t(`${tl}.constructionPlanName`)} value={constructionPlan?.name} />
            <DetailsRow
              label={t(`${tl}.constructionPlanSituationNumber`)}
              value={constructionPlan?.situationNumber}
            />
          </TemplateDetails>
        )}
      </Grid>
      <Grid xs={12} md={6} item px={3} py={2}>
        {!valuation || !Object.values(valuation).length ? null : (
          <TemplateDetails title={t(`${tl}.valuationTitle`)}>
            <DetailsRow
              label={t(`${tl}.valuationType`)}
              value={generalEnum(
                'realEstateValuationType',
                valuation?.type,
                RealEstateValuationTypeEnum
              )}
            />
            <DetailsRow type="date" label={t(`${tl}.date`)} value={valuation?.date} />
            <DetailsRow
              type="currency"
              label={t(`${tl}.marketValue`)}
              value={valuation?.marketValue}
            />
            <DetailsRow
              type="currency"
              label={t(`${tl}.forcedSaleValue`)}
              value={valuation?.forcedSaleValue}
            />
          </TemplateDetails>
        )}

        <TemplateDetails title={t(`${tl}.energyLabelTitle`)}>
          <DetailsRow label={t(`${tl}.energyLabelValue`)}>
            <EnergyLabel value={energyEfficiencyReport?.energyLabel} />
          </DetailsRow>
          <DetailsRow
            type="date"
            label={t(`${tl}.energyLabelDate`)}
            value={energyEfficiencyReport?.date}
          />
        </TemplateDetails>
      </Grid>
    </Grid>
  );
};

export default RealEstateDetails;
